import React, { useState, useEffect } from 'react';
import {
    Card,
     Input,
    Tag,
    Space,
    Modal,
    message,
    Popconfirm,
    Typography,
    Row,
    Col,
    Divider,
    Spin
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SaveOutlined,
    CloseOutlined
} from '@ant-design/icons';
import CreatableSelect from 'react-select/creatable';
import categoryService from '@/services/category.service';
import InspectionBtn from '@/components/InspectionBtn';
import toast from "react-hot-toast";

const { Title, Text } = Typography;

export default function TextRecommendation() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ category_key: '', category_items: [], items: [] });
    const [newItemInputs, setNewItemInputs] = useState({});
    const [editingItems, setEditingItems] = useState({});
    const [existingCategoryNames, setExistingCategoryNames] = useState([]);




    // Load categories from API
    const loadCategories = async () => {
        setLoading(true);
        try {
            const response = await categoryService.getAllcategory();
            setCategories(response.categories || []);

            // Extract all category names from all categories for react-select
            const allCategoryNames = [];
            response.categories.forEach(cat => {
                if (Array.isArray(cat.category_items)) {
                    cat.category_items.forEach(name => {
                        if (!allCategoryNames.some(item => item.value === name)) {
                            allCategoryNames.push({
                                value: name,
                                label: name
                            });
                        }
                    });
                }
            });
            setExistingCategoryNames(allCategoryNames);
        } catch (error) {
            toast.error(`Failed to load categories: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Load categories on component mount
    useEffect(() => {
        loadCategories();
    }, []);

    // Create new category
    const createCategory = async (categoryData) => {
        try {
            const categoryRes = await categoryService.addCategory(categoryData);
            toast.success('Category created successfully!');
            await loadCategories(); // Reload categories
            return true;
        } catch (error) {
            toast.error(`Failed to create category: ${error.message}`);
            return false;
        }
    };

    // Update category
    const updateCategory = async (categoryId, categoryData) => {
        try {
            await categoryService.updateCategory(categoryId, categoryData);


            toast.success('Category updated successfully!');
            await loadCategories(); // Reload categories
            return true;
        } catch (error) {
            toast.error(`Failed to update category: ${error.message}`);
            return false;
        }
    };

    // Delete category
    const deleteCategory = async (categoryId) => {
        try {
            await categoryService.deleteCategory(categoryId);
            toast.success('Category deleted successfully!');
            await loadCategories(); // Reload categories
        } catch (error) {
            toast.error(`Failed to delete category: ${error.message}`);
        }
    };



    // Update item in category
    const updateItemInCategory = async (categoryId, oldItem, newItem) => {
        try {
            await apiCall(`/categories/${categoryId}/items`, {
                method: 'PUT',
                body: JSON.stringify({ oldItem, newItem }),
            });
            toast.success('Item updated successfully!');
            await loadCategories(); // Reload categories
        } catch (error) {
            toast.error(`Failed to update item: ${error.message}`);
        }
    };

    // Remove item from category
    const removeItemFromCategory = async (categoryId, item) => {
        try {
            await apiCall(`/categories/${categoryId}/items/${encodeURIComponent(item)}`, {
                method: 'DELETE',
            });
            toast.success('Item removed successfully!');
            await loadCategories(); // Reload categories
        } catch (error) {
            toast.error(`Failed to remove item: ${error.message}`);
        }
    };

    // Handle add category
    const handleAddCategory = () => {
        setEditingCategory(null);
        setFormData({ category_key: '', category_items: [], items: [] });
        setIsModalVisible(true);
    };

    // Handle edit category
    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setFormData({
            category_key: category.category_key || category.category_items?.[0]?.toLowerCase().replace(/[^a-z0-9]/g, '_') || '',
            category_items: category.category_items || [],
            items: category.items || []
        });
        setIsModalVisible(true);
    };

    // Handle delete category
    const handleDeleteCategory = async (categoryId) => {
        await deleteCategory(categoryId);
    };

    // Handle modal OK
    const handleModalOk = async () => {
        if (!formData.category_key || !formData.category_key.trim()) {
            toast.error('Please enter a category key');
            return;
        }

        if (!formData.category_items || formData.category_items.length === 0) {
            toast.error('Please add at least one category name');
            return;
        }

        // Validate category key format
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(formData.category_key.trim())) {
            toast.error('Category key must start with letter or underscore and contain only letters, numbers, and underscores');
            return;
        }

        const categoryData = {
            category_key: formData.category_key.trim(),
            category_items: formData.category_items,
            items: formData.items || []
        };

        let success = false;

        if (editingCategory) {
            // Update existing category
            success = await updateCategory(editingCategory.id, categoryData);
        } else {
            // Create new category
            success = await createCategory(categoryData);
        }

        if (success) {
            setIsModalVisible(false);
            setFormData({ category_key: '', category_items: [], items: [] });
        }
    };

    // Handle add item
    const handleAddItem = async (categoryId) => {
         const newItem = newItemInputs[categoryId];
         if (!newItem || !newItem.trim()) {
            toast.error('Please enter an item name');
            return;
        }
        const getcategory = categories?.find(x => x?.id === categoryId);
        const cateogoryPayload = {
            category_key: getcategory.category_key,
            category_items: getcategory.category_items.concat(newItem)


        }
         await categoryService.updateCategory(categoryId, cateogoryPayload)
        loadCategories()
    };

    // Handle delete item
    const handleDeleteItem = async (categoryId, item) => {
        await removeItemFromCategory(categoryId, item);
    };

    // Handle edit item
    const handleEditItem = (categoryId, itemIndex, item) => {
        setEditingItems({
            ...editingItems,
            [`${categoryId}-${itemIndex}`]: item
        });
    };

    // Handle save item
    const handleSaveItem = async (categoryId, itemIndex, oldItem) => {

         const newValue = editingItems[`${categoryId}-${itemIndex}`];
        if (!newValue || !newValue.trim()) {
            toast.error('Item name cannot be empty');
            return;
        }

        await updateItemInCategory(categoryId, oldItem, newValue.trim());

        const newEditingItems = { ...editingItems };
        delete newEditingItems[`${categoryId}-${itemIndex}`];
        setEditingItems(newEditingItems);
    };

    // Handle cancel edit
    const handleCancelEdit = (categoryId, itemIndex) => {
        const newEditingItems = { ...editingItems };
        delete newEditingItems[`${categoryId}-${itemIndex}`];
        setEditingItems(newEditingItems);
    };

    // Handle category names change in CreatableSelect
    const handleCategoryNamesChange = (selectedOptions) => {
        const category_items = selectedOptions ? selectedOptions.map(option => option.value) : [];

        setFormData({
            ...formData,
            category_items: category_items,
            // Auto-generate category key if it's empty and we have at least one name
            category_key: formData.category_key || (category_items.length > 0 ?
                category_items[0].toLowerCase().replace(/[^a-z0-9]/g, '_') : '')
        });
    };

    // Handle category key change
    const handleCategoryKeyChange = (e) => {
        setFormData({
            ...formData,
            category_key: e.target.value
        });
    };

    // Helper function to display category names
    const displayCategoryNames = (category_items) => {
        if (!category_items || category_items.length === 0) return 'Unnamed Category';
        if (category_items.length === 1) return category_items[0];
        return `${category_items[0]} (+${category_items.length - 1} more)`;
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className=" mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <Title level={4} className="!mb-0">Text Recommendation</Title>
                    <Space>
                        <InspectionBtn
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddCategory}
                            className="bg-green-500 hover:bg-green-600"
                        >
                            Add Category
                        </InspectionBtn>
                    </Space>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center min-h-64">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Row gutter={[16, 16]}>
                        {categories.length > 0 ? categories.map((category) => (
                            <Col key={category.id} xs={24} lg={12} xl={8}>
                                <Card
                                    title={
                                        <div>
                                            <div className="font-semibold">{displayCategoryNames(category.category_items)}</div>
                                            {category.category_items && category.category_items.length > 1 && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {category.category_items.slice(1).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    }
                                    extra={
                                        <Space>
                                            <InspectionBtn
                                                type="text"
                                                icon={<EditOutlined />}
                                                size="small"
                                                onClick={() => handleEditCategory(category)}
                                            />
                                            <Popconfirm
                                                title="Are you sure you want to delete this category?"
                                                onConfirm={() => handleDeleteCategory(category.id)}
                                                okText="Yes"
                                                cancelText="No"
                                            >
                                                <InspectionBtn
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    size="small"
                                                />
                                            </Popconfirm>
                                        </Space>
                                    }
                                    className="h-full shadow-md hover:shadow-lg transition-shadow"
                                >
                                    <div className="space-y-3">
                                        {/* Display all category names as tags */}
                                        {category.category_items && category.category_items.length > 0 && (
                                            <div>
                                                <Text type="secondary" className="text-xs">Category Names:</Text>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {category.category_items.map((name, idx) => (
                                                        <Tag key={idx} color="blue" size="small">
                                                            {name}
                                                        </Tag>
                                                    ))}
                                                </div>
                                            </div>
                                        )}


                                        {/* Existing Items */}
                                        <div>
                                            <div className="flex flex-wrap gap-2 mt-1">
                                                {(category.items || []).map((item, index) => {
                                                    const editKey = `${category.id}-${index}`;
                                                    const isEditing = editingItems.hasOwnProperty(editKey);

                                                    return (
                                                        <div key={index} className="flex items-center gap-1">
                                                            {isEditing ? (
                                                                <Space.Compact>
                                                                    <Input
                                                                        size="small"
                                                                        value={editingItems[editKey]}
                                                                        onChange={(e) => handleEditItem(category.id, index, e.target.value)}
                                                                        onPressEnter={() => handleSaveItem(category.id, index, item)}
                                                                        className="w-32"
                                                                    />
                                                                    <InspectionBtn
                                                                        type="primary"
                                                                        size="small"
                                                                        icon={<SaveOutlined />}
                                                                        onClick={() => handleSaveItem(category.id, index, item)}
                                                                    />
                                                                    <InspectionBtn
                                                                        size="small"
                                                                        icon={<CloseOutlined />}
                                                                        onClick={() => handleCancelEdit(category.id, index)}
                                                                    />
                                                                </Space.Compact>
                                                            ) : (
                                                                <Tag
                                                                    closable
                                                                    onClose={() => handleDeleteItem(category.id, item)}
                                                                    onClick={() => handleEditItem(category.id, index, item)}
                                                                    className="cursor-pointer hover:bg-green-50 transition-colors"
                                                                >
                                                                    {item}
                                                                </Tag>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <Divider className="!my-3" />

                                        {/* Add New Item */}
                                        <Space.Compact className="w-full">
                                            <Input
                                                placeholder="Add new item..."
                                                value={newItemInputs[category.id] || ''}
                                                onChange={(e) => setNewItemInputs({
                                                    ...newItemInputs,
                                                    [category.id]: e.target.value
                                                })}
                                                onPressEnter={() => handleAddItem(category.id)}
                                            />
                                            <InspectionBtn
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={() => handleAddItem(category.id)}
                                                className="bg-green-500 hover:bg-green-600"
                                            >
                                                Add
                                            </InspectionBtn>
                                        </Space.Compact>
                                    </div>
                                </Card>
                            </Col>
                        )) : (
                            <Col span={24}>
                                <div className="text-center py-8">
                                    <Text type="secondary">No categories found. Create your first category!</Text>
                                </div>
                            </Col>
                        )}
                    </Row>
                )}

                {/* Add/Edit Category Modal */}
                <Modal
                    title={editingCategory ? 'Edit Category' : 'Add New Category'}
                    open={isModalVisible}
                    onOk={handleModalOk}
                    onCancel={() => {
                        setIsModalVisible(false);
                        setFormData({ category_key: '', category_items: [], items: [] });
                    }}
                    okText={editingCategory ? 'Update' : 'Add'}
                    className="top-20"
                >
                    <div className="space-y-4 mt-4">
                        <div>
                            <Text strong className="block mb-2">Category Key</Text>
                            <Input
                                placeholder="e.g., yesno, quality_control, condition_assessment"
                                value={formData.category_key}
                                onChange={handleCategoryKeyChange}
                            />

                        </div>

                        <div>
                            <Text strong className="block mb-2">Category Names</Text>
                            <CreatableSelect
                                isMulti
                                isClearable
                                placeholder="Select or create category names..."
                                options={existingCategoryNames}
                                value={formData.category_items.map(name => ({ value: name, label: name }))}
                                onChange={handleCategoryNamesChange}
                                formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '32px',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '6px',
                                        '&:hover': {
                                            borderColor: '#40a9ff'
                                        }
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 9999
                                    })
                                }}
                            />

                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
}